import { z } from "zod";

/**
 * Budget health (ADP-89) — a derived read model shared by the API, frontend and
 * AI decision context. The backend assembles it so no consumer re-derives budget
 * pacing from raw spend/budget fields.
 *
 * MVP is DERIVED: "budget_limited" is inferred from spend reaching the budget
 * (Amazon's authoritative out-of-budget signal is not ingested yet), so
 * `derived` is always true and consumers should phrase it as an estimate.
 */
export const budgetHealthStateSchema = z.enum([
  "normal", // spend comfortably under budget
  "near_exhaustion", // spend approaching the budget cap
  "budget_limited", // spend at/over budget — ads likely throttled, missing impressions
  "unknown", // no budget set, no spend data, currency mismatch, or non-daily cap
]);
export type BudgetHealthState = z.infer<typeof budgetHealthStateSchema>;

/** Why a health reading is `unknown` — drives the frontend tooltip and AI context. */
export const budgetHealthReasonSchema = z.enum([
  "no_budget",
  "no_spend_data",
  "currency_mismatch",
  "non_daily_budget",
  // A dateRange portfolio budget whose window has not started yet — not active,
  // so it must not read as a healthy `normal` at zero spend.
  "budget_not_active",
]);
export type BudgetHealthReason = z.infer<typeof budgetHealthReasonSchema>;

export const budgetHealthSchema = z.object({
  state: budgetHealthStateSchema,
  /** spend ÷ budget in the same window; null when `state` is `unknown`. */
  utilization: z.number().nullable(),
  /** Spend in the comparison window (today for campaigns; period-to-date for portfolios). */
  spend: z.number().nullable(),
  budget: z.number().nullable(),
  currency: z.string().nullable(),
  /** Always true in the MVP — inferred, not Amazon's authoritative signal. */
  derived: z.boolean(),
  reason: budgetHealthReasonSchema.nullable(),
  /**
   * ADP-220 (campaign grain only): the reporting day the spend was read for
   * and the marketplace zone that day was resolved in (null → UTC fallback),
   * plus when that day's report row was last synced (null → no row yet).
   */
  asOfDate: z.string().nullable().optional(),
  timeZone: z.string().nullable().optional(),
  spendSyncedAt: z.string().nullable().optional(),
});
export type BudgetHealth = z.infer<typeof budgetHealthSchema>;
